import type { FC } from "react";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader, Trash2 } from "lucide-react";

import type { ContractContactGroup } from "../types";

import { useDeleteOccupantService } from "../services/use-delete-occupant-service";
import { serverErrorMessage } from "../utils/apply-server-errors";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  contractId: string | number;
  /** The contact block to remove; `null` keeps the dialog closed. */
  group: ContractContactGroup | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirms removing a contact from the contract — every alert they hold on it
 * goes with them. Names the person, so the trash icon on a block of rows is
 * never ambiguous about whom it deletes.
 */
const DeleteOccupantDialog: FC<Props> = ({ contractId, group, onOpenChange }) => {
  const { t } = useTranslation(["occupants"]);
  const [error, setError] = useState<string | null>(null);
  const { mutate: deleteOccupant, isPending: deleting } = useDeleteOccupantService(contractId);

  const handleOpenChange = (open: boolean) => {
    if (!open && deleting) return;
    if (!open) setError(null);
    onOpenChange(open);
  };

  const handleConfirm = () => {
    if (!group) return;

    setError(null);
    deleteOccupant(
      group.contractContacts.map((contractContact) => String(contractContact.id)),
      {
        // Bypass handleOpenChange: its `deleting` guard would block this close.
        onSuccess: () => onOpenChange(false),
        onError: (err) =>
          setError(
            serverErrorMessage(err) ??
              t("occupants:occupants-delete-failed", "Failed to remove the contact. Please try again."),
          ),
      },
    );
  };

  return (
    <Dialog open={group !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-lg p-9 text-center sm:max-w-[420px]">
        <DialogHeader className="items-center gap-8">
          <Trash2 className="size-14 text-destructive" strokeWidth={1.5} />
          <div className="flex w-full flex-col gap-3">
            <DialogTitle className="text-center text-lg font-semibold leading-normal text-foreground">
              {t("occupants:occupants-delete-title", "Are you sure you want to delete {{name}}?", {
                name: group?.name || t("occupants:occupants-unnamed-contact", "Unnamed contact"),
              })}
            </DialogTitle>
            <DialogDescription className="text-center text-sm text-muted-foreground">
              {t("occupants:occupants-delete-description", "This action is irreversible")}
            </DialogDescription>
          </div>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Side by side and centred at every width, as the design lays them out. */}
        <DialogFooter className="flex-row justify-center gap-3 sm:justify-center">
          <Button
            className="cursor-pointer"
            disabled={deleting}
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            {t("occupants:occupants-cancel", "Cancel")}
          </Button>
          <Button
            className="cursor-pointer"
            disabled={deleting}
            type="button"
            variant="destructive"
            onClick={handleConfirm}
          >
            {deleting && <Loader className="size-4 animate-spin" />}
            {t("occupants:occupants-delete-confirm", "Yes, delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteOccupantDialog;
